import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback component handles the OAuth callback from Emergent Auth.
 * Processes the session_id from URL fragment and exchanges it for a local session.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { processGoogleSession, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        console.error('No session_id found in URL');
        navigate('/login', { 
          state: { error: 'Authentication failed. Please try again.' },
          replace: true 
        });
        return;
      }

      try {
        // Exchange session_id for local session
        await processGoogleSession(sessionId);
        
        // Clear the URL fragment
        window.history.replaceState(null, '', '/auth/callback');
        
        // Check if there's a pending beta code to apply
        const pendingBetaCode = localStorage.getItem('pending_beta_code');
        if (pendingBetaCode) {
          try {
            // Apply beta code to user
            const storedToken = localStorage.getItem('auth_token');
            await fetch(`${API_URL}/api/users/apply-beta-code`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {})
              },
              credentials: 'include',
              body: JSON.stringify({ beta_code: pendingBetaCode })
            });
          } catch (betaError) {
            console.error('Failed to apply beta code:', betaError);
          } finally {
            localStorage.removeItem('pending_beta_code');
          }
        }
        
        // Small delay to ensure cookie is set, then verify auth
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Re-check auth to ensure state is synchronized with cookie
        await checkAuth();
        
        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Failed to process Google session:', error);
        navigate('/login', { 
          state: { error: error.message || 'Authentication failed. Please try again.' },
          replace: true 
        });
      }
    };

    processSession();
  }, [navigate, processGoogleSession, checkAuth]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
        <p className="text-gray-600">Logging you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
